import { useEffect, useMemo, useState } from 'react';
import {
  BerichtError,
  createBericht,
  uploadBerichtFoto,
  type CreateBerichtResult,
  type Prioriteit,
  type Richting,
} from '../../lib/berichten';
import {
  fetchWasserijItemsForVervolg,
  type WasserijItemForPicker,
} from '../../lib/klachten';
import { searchItems, type WasserijItemListRow } from '../../lib/wasserij-items';

interface Props {
  /** Pre-set scope (inline op order/item-detail). Leeg = algemeen bericht. */
  werkOrderId?: string | null;
  wasserijItemId?: string | null;
  werkOrderName?: string | null;
  /** Reparatie-opdracht-scope (chat met de reparateur). Wint van WO/WI-scope. */
  reparatieOpdrachtId?: string | null;
  /** Standaard office_to_field (kantoor → uitvoering). */
  richting?: Richting;
  onClose: () => void;
  onCreated: (result: CreateBerichtResult) => void;
}

const NO_ITEM = '__none__';

function itemLabel(item: WasserijItemForPicker): string {
  const dim =
    item.lengteCm && item.breedteCm
      ? ` — ${item.lengteCm}×${item.breedteCm} cm`
      : item.diameterCm
        ? ` — ⌀${item.diameterCm} cm`
        : '';
  return `${item.naam}${dim}`;
}

/**
 * Composer voor een nieuw bericht (kantoor → uitvoering).
 * - Met `werkOrderId`: toont een Wasserij_item-kiezer binnen die werkbon.
 * - Zonder scope: algemeen bericht.
 */
export function BerichtComposer({
  werkOrderId = null,
  wasserijItemId: initialWasserijItemId = null,
  werkOrderName,
  reparatieOpdrachtId = null,
  richting = 'office_to_field',
  onClose,
  onCreated,
}: Props) {
  const isReparatie = !!reparatieOpdrachtId;
  const peer: 'reparateur' | 'uitvoering' =
    richting === 'office_to_reparateur' || isReparatie ? 'reparateur' : 'uitvoering';
  const [prioriteit, setPrioriteit] = useState<Prioriteit>('normaal');
  const [onderwerp, setOnderwerp] = useState('');
  const [body, setBody] = useState('');
  const [fotoPaths, setFotoPaths] = useState<string[]>([]);
  const [fotoPreviews, setFotoPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasWerkOrder = !!werkOrderId && !isReparatie;
  const [selectedItemId, setSelectedItemId] = useState<string>(initialWasserijItemId ?? NO_ITEM);
  const [items, setItems] = useState<WasserijItemForPicker[]>([]);
  const [itemsLoading, setItemsLoading] = useState(hasWerkOrder);

  // Algemeen bericht (geen vooraf-gekozen WO): optioneel een item zoeken+koppelen.
  const [itemQuery, setItemQuery] = useState('');
  const [itemResults, setItemResults] = useState<WasserijItemListRow[]>([]);
  const [searchingItems, setSearchingItems] = useState(false);
  const [pickedItem, setPickedItem] = useState<WasserijItemListRow | null>(null);

  useEffect(() => {
    if (hasWerkOrder || pickedItem) return;
    const q = itemQuery.trim();
    if (q.length < 2) {
      setItemResults([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setSearchingItems(true);
      searchItems(q, controller.signal)
        .then((rows) => setItemResults(rows.slice(0, 8)))
        .catch(() => {
          /* abort / fout → laat resultaten leeg */
        })
        .finally(() => setSearchingItems(false));
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [itemQuery, hasWerkOrder, pickedItem]);

  useEffect(() => {
    if (!werkOrderId) return;
    let cancelled = false;
    setItemsLoading(true);
    fetchWasserijItemsForVervolg(werkOrderId)
      .then((rows) => {
        if (cancelled) return;
        setItems(rows);
        if (initialWasserijItemId && !rows.some((r) => r.id === initialWasserijItemId)) {
          setSelectedItemId(NO_ITEM);
        }
      })
      .catch(() => {
        /* picker niet beschikbaar → bericht gaat op WO-niveau */
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [werkOrderId, initialWasserijItemId]);

  const scopeLabel = useMemo(() => {
    if (isReparatie) {
      return 'Bericht aan de reparateur over deze opdracht';
    }
    if (!hasWerkOrder) {
      return pickedItem
        ? `${pickedItem.itemNumber} · ${pickedItem.orderNumber}`
        : 'Algemeen bericht — optioneel koppelen aan item';
    }
    const item = selectedItemId !== NO_ITEM ? items.find((i) => i.id === selectedItemId) : null;
    return item
      ? `${item.naam} (${werkOrderName ?? werkOrderId})`
      : `Hele werkbon (${werkOrderName ?? werkOrderId})`;
  }, [isReparatie, hasWerkOrder, pickedItem, selectedItemId, items, werkOrderName, werkOrderId]);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const result = await uploadBerichtFoto(file);
        setFotoPaths((prev) => [...prev, result.path]);
        setFotoPreviews((prev) => [...prev, result.url]);
      }
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Foto-upload mislukt');
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) {
      setError('Bericht mag niet leeg zijn');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await createBericht({
        werkOrderId: isReparatie ? null : hasWerkOrder ? werkOrderId : null,
        wasserijItemId: isReparatie
          ? null
          : hasWerkOrder
            ? selectedItemId !== NO_ITEM
              ? selectedItemId
              : null
            : (pickedItem?.id ?? null),
        reparatieOpdrachtId: reparatieOpdrachtId ?? null,
        onderwerp: onderwerp.trim() || null,
        prioriteit,
        body: body.trim(),
        fotoUrls: fotoPaths,
        richting,
      });
      onCreated(result);
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Versturen mislukt');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Nieuw bericht aan {peer}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{scopeLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-xl leading-none"
            aria-label="Sluiten"
          >
            ×
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {hasWerkOrder && (
            <label className="block">
              <span className="text-xs text-slate-600">Voor welk item?</span>
              <select
                value={selectedItemId}
                onChange={(e) => setSelectedItemId(e.target.value)}
                disabled={itemsLoading}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value={NO_ITEM}>📋 Hele werkbon (geen specifiek item)</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {itemLabel(item)}
                  </option>
                ))}
              </select>
              {itemsLoading && <p className="mt-1 text-[11px] text-slate-500">Items laden…</p>}
            </label>
          )}

          {!hasWerkOrder && !isReparatie && (
            <div className="relative">
              <span className="text-xs text-slate-600">Koppel aan item (optioneel)</span>
              {pickedItem ? (
                <div className="mt-1 flex items-center justify-between gap-2 border border-slate-300 rounded px-2 py-1.5 bg-slate-50">
                  <span className="text-sm text-slate-800 truncate">
                    {pickedItem.itemNumber} · {pickedItem.productName}
                    {pickedItem.dimensionsLabel && pickedItem.dimensionsLabel !== '—'
                      ? ` · ${pickedItem.dimensionsLabel}`
                      : ''}
                    {' · '}
                    {pickedItem.orderNumber}
                    {pickedItem.customerName ? ` · ${pickedItem.customerName}` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setPickedItem(null);
                      setItemQuery('');
                      setItemResults([]);
                    }}
                    className="text-slate-400 hover:text-slate-700 text-base leading-none shrink-0"
                    aria-label="Koppeling verwijderen"
                  >
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={itemQuery}
                    onChange={(e) => setItemQuery(e.target.value)}
                    placeholder="Zoek op WI-nummer, kledtype of klantnaam…"
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                  {itemQuery.trim().length >= 2 && (
                    <ul className="absolute z-10 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg">
                      {searchingItems && itemResults.length === 0 && (
                        <li className="px-3 py-2 text-xs text-slate-500">Zoeken…</li>
                      )}
                      {!searchingItems && itemResults.length === 0 && (
                        <li className="px-3 py-2 text-xs text-slate-400">Geen items gevonden</li>
                      )}
                      {itemResults.map((it) => (
                        <li key={it.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setPickedItem(it);
                              setItemResults([]);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50"
                          >
                            <span className="block text-sm font-medium text-slate-800 truncate">
                              {it.itemNumber} · {it.productName}
                            </span>
                            <span className="block text-[11px] text-slate-500 truncate">
                              {it.dimensionsLabel && it.dimensionsLabel !== '—' ? `${it.dimensionsLabel} · ` : ''}
                              {it.orderNumber} · {it.customerName}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="block col-span-1">
              <span className="text-xs text-slate-600">Onderwerp (optioneel)</span>
              <input
                type="text"
                value={onderwerp}
                onChange={(e) => setOnderwerp(e.target.value)}
                maxLength={255}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                placeholder="Korte titel"
              />
            </label>
            <label className="block col-span-1">
              <span className="text-xs text-slate-600">Prioriteit</span>
              <select
                value={prioriteit}
                onChange={(e) => setPrioriteit(e.target.value as Prioriteit)}
                className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="normaal">Normaal</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-xs text-slate-600">Bericht</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={5000}
              rows={5}
              required
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              placeholder={`Wat moet de ${peer} weten of doen?`}
            />
          </label>

          <div>
            <span className="text-xs text-slate-600">Foto's (optioneel)</span>
            <div className="mt-1 flex flex-wrap gap-2">
              {fotoPreviews.map((url, idx) => (
                <div key={fotoPaths[idx]} className="relative">
                  <img src={url} alt="" className="w-16 h-16 object-cover rounded border border-slate-200" />
                  <button
                    type="button"
                    onClick={() => {
                      setFotoPaths((prev) => prev.filter((_, i) => i !== idx));
                      setFotoPreviews((prev) => prev.filter((_, i) => i !== idx));
                    }}
                    className="absolute -top-1 -right-1 bg-white border border-slate-300 rounded-full w-5 h-5 text-xs leading-none hover:bg-red-50"
                  >
                    ×
                  </button>
                </div>
              ))}
              <label
                className={`w-16 h-16 border-2 border-dashed border-slate-300 rounded flex items-center justify-center text-2xl text-slate-400 cursor-pointer hover:border-indigo-400 hover:text-indigo-500 ${
                  uploading ? 'opacity-50 cursor-wait' : ''
                }`}
              >
                +
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  disabled={uploading}
                />
              </label>
            </div>
            {uploading && <p className="mt-1 text-xs text-slate-500">Foto uploaden…</p>}
          </div>

          {error && (
            <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
        </form>

        <footer className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || uploading || !body.trim()}
            className="bg-indigo-600 text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {saving ? 'Versturen…' : 'Versturen'}
          </button>
        </footer>
      </div>
    </div>
  );
}
